<template>
  <div id="automatic-recharge-edit">
    <a
      class="link"
      href="#"
      @click.prevent="openEditAutomaticRechargeModal()">
      Editar
    </a>
    <div
      class="modal fade"
      tabindex="-1"
      role="dialog"
      id="editAutomaticRechargeModal"
      ref="editAutomaticRechargeModal"
      data-keyboard="false"
      data-backdrop="static"
      aria-hidden="true">
      <div
        class="modal-dialog modal-lg modal-dialog-centered ar-edit-modal"
        role="document">
        <div class="modal-content">
          <div class="modal-header ar-edit-modal-header">
            <div class="ar-edit-modal-header-left">
              <Icon name="sync-plus" :size="28" icon-class="ar-edit-modal-header-icon" />
              <h5 class="modal-title">Editar Recarga Automática</h5>
            </div>
            <button
              type="button"
              class="close"
              @click.prevent="closeEditAutomaticRechargeModal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body text-center ar-edit-modal-body">
            <ErrorAlert :error="error" />
            <div class="form-group d-flex justify-content-center align-items-center">
              <h5 class="mb-0 align-items-center automatic-recharge-label">
                Ativar quando o saldo for <strong>menor</strong> que
                <i
                  id="info-icon"
                  class="ik ik-info ml-2"
                  style="cursor: pointer;"
                />
              </h5>
              <b-tooltip
                placement="top"
                target="info-icon"
              >
                Quando seu saldo chegar a este valor, uma nova recarga será feita automaticamente.
              </b-tooltip>
            </div>
            <div class="form-group row">
              <div class="col-sm-12 minimumAmount automaticRechargeAmount">
                <CurrencyAmountInput
                  ref="minimumAmount"
                  v-model="minimumAmountNumber"
                  :min="5"
                  :max="5000"
                  size="compact"
                  @enter="automaticRechargeEdit" />
              </div>
            </div>
            <div class="form-group">
              <h5 class="automatic-recharge-label">Informe o <b>valor</b> que deseja recarregar</h5>
            </div>
            <div class="form-group row">
              <div class="col-sm-12 rechargeAmount automaticRechargeAmount">
                <CurrencyAmountInput
                  ref="rechargeAmount"
                  v-model="rechargeAmountNumber"
                  :min="250"
                  :max="50000"
                  size="compact"
                  @enter="automaticRechargeEdit" />
              </div>
            </div>
            <div v-if="hasDynamicTariff">
              <div class="form-group">
                <p class="automatic-recharge-discount-label">Ou selecione o <b>desconto</b> deseja receber</p>
              </div>
              <div class="form-group row">
                <div :class="`col automaticRechargeSliderEdit`">
                  <div
                    class="range-slider"
                    @click.prevent="sliderClick">
                    <input
                      class="form-control"
                      id="automaticRechargeSliderEdit"
                      type="input"
                      data-slider-tooltip="hide"
                      :data-slider-ticks-snap-bounds="sliderTicksSnapBounds"
                      :data-slider-ticks-labels="sliderTicksLabels"
                      :data-ticks_positions="sliderTicksPositions">
                  </div>
                </div>
              </div>
              <div class="form-group automatic-recharge-prices">
                <div class="row">
                  <div class="col-lg-6"><span class="automatic-recharge-price-label">Minuto Móvel</span></div>
                  <div class="col-lg-6"><span class="automatic-recharge-price-label">Minuto Fixo</span></div>
                </div>
                <div class="row">
                  <div class="col-sm-6"><span class="automatic-recharge-price-value">R$ {{ mobilePrice }}</span></div>
                  <div class="col-sm-6"><span class="automatic-recharge-price-value">R$ {{ fixedlinePrice }}</span></div>
                </div>
              </div>
              <div class="automatic-recharge-economy">
                <p v-if="rechargeAmountNumber >= 500">
                  Com esse desconto a cada <b>1000 minutos</b> você economizará em média <b>R$ {{ averageSaving }},00</b>
                </p>
              </div>
            </div>
            <hr>
            <CompensationTermsMethods
              title="Prazos de compensação"
              description="Após a compensação do pagamento, seus créditos serão adicionados automaticamente à sua conta." />
          </div>

          <div class="modal-footer ar-edit-modal-footer">
            <div class="buttons">
              <button
                type="button"
                class="btn ar-edit-btn-cancel"
                @click.prevent="closeEditAutomaticRechargeModal">Cancelar</button>
              <button
                type="button"
                class="btn ar-edit-btn-submit"
                :disabled="(error && (error.message || error.lines)) ||
                (!rechargeAmountNumber || !minimumAmountNumber)"
                @click.prevent="automaticRechargeEdit">Alterar Recarga Automática</button>
              <PlansCallToAction @click.prevent="closeEditAutomaticRechargeModal" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import loopback from '@/services/loopback'
import { EventBus } from '@/services/event-bus'
import PlansCallToAction from '@/components/Common/PlansCallToAction.vue'
import CompensationTermsMethods from '@/components/Common/CompensationTermsMethods.vue'
import CurrencyAmountInput from '@/components/Common/CurrencyAmountInput.vue'
import Icon from '@/components/Common/Icon.vue'
const MOBILE_PRICES = ['0,38', '0,36', '0,33', '0,28', '0,22', '0,15']
const FIXEDLINE_PRICE = '0,09'
const CHARGE_AMOUNTS = [250, 500, 1000, 2000, 3000, 5000]
const CHARGE_AMOUNT_TIERS = [0, 5, 15, 30, 45, 65]
const CHARGE_AMOUNT_POSITIONS = [0, 20, 40, 60, 80, 100]
const MIN_RECHARGE_AMOUNT = 250
const MAX_RECHARGE_AMOUNT = 50000
const RECHARGE_AMOUNT_RANGE_MESSAGE = 'Recarga mínima é de R$ 250,00 e máxima de R$ 50.000,00'

const LEGACY_MOBILE_PRICES = ['0,35', '0,33', '0,30', '0,25', '0,19', '0,12']
const LEGACY_FIXEDLINE_PRICES = ['0,08', '0,079', '0,078', '0,076', '0,074', '0,07']

const NEW_MOBILE_PRICES = ['0,41', '0,39', '0,36', '0,31', '0,25', '0,18']
const NEW_FIXEDLINE_PRICE = '0,09' // TEL-2294: sem valor próprio definido, assumido igual ao "Atual"

export default {
  name: 'AutomaticRechargeEdit',
  components: {
    Icon,
    PlansCallToAction,
    CompensationTermsMethods,
    CurrencyAmountInput
  },
  props: {
    id: {
      type: Number,
      default: 1
    },
    minimumAmount: {
      type: String,
      default: '100,00'
    },
    rechargeAmount: {
      type: String,
      default: '250,00'
    }
  },
  data () {
    return {
      sliderTicks: CHARGE_AMOUNT_POSITIONS,
      sliderTicksPositions: CHARGE_AMOUNT_POSITIONS,
      sliderTicksLabels: CHARGE_AMOUNT_TIERS.map(v => `${v}%`),
      sliderTicksSnapBounds: 20,
      error: null,
      minimumAmountNumber: null,
      rechargeAmountNumber: null,
      isRateplanLegacy: typeof this.$store.state.auth.organization.isRateplanLegacy === 'boolean'
        ? this.$store.state.auth.organization.isRateplanLegacy
        : true,
      isRatePlanNew: typeof this.$store.state.auth.organization.isRatePlanNew === 'boolean'
        ? this.$store.state.auth.organization.isRatePlanNew
        : false,
      hasDynamicTariff: this.$store.state.auth.softswitchIntegration &&
        typeof this.$store.state.auth.softswitchIntegration.metadata.hasDynamicTariff === 'boolean'
        ? this.$store.state.auth.softswitchIntegration.metadata.hasDynamicTariff
        : true
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.minimumAmountNumber = this.parseAmountToNumber(this.minimumAmount)
      this.rechargeAmountNumber = this.parseAmountToNumber(this.rechargeAmount)
      this.buildSlider()
    })
  },
  methods: {
    automaticRechargeEdit () {
      if (!this.isRechargeAmountInRange()) {
        this.error = { message: RECHARGE_AMOUNT_RANGE_MESSAGE }
        return
      }
      const payload = {
        minimumAmount: this.minimumAmountNumber,
        rechargeAmount: this.rechargeAmountNumber
      }
      loopback.patch(`/automatic-recharges/${this.id}`, payload)
        .then(() => {
          this.error = null
          this.closeEditAutomaticRechargeModal()
          EventBus.$emit('reload-automatic-recharge')
          EventBus.$emit('automatic-recharge-success', {
            minimumAmount: this.minimumAmountNumber,
            rechargeAmount: this.rechargeAmountNumber,
            source: 'edit'
          })
        })
        .catch(err => {
          if (err.message && err.message.includes('has the same values')) {
            this.error = {
              message: 'Os valores informados são iguais aos atuais.'
            }
            return
          }
          // TEL-2243: o texto vem do normalizador; o fallback e da propria tela.
          // `err.message` nunca chega aqui — e do backend, em ingles.
          if (err.friendly && Array.isArray(err.friendly.lines) && err.friendly.code !== 'UNKNOWN') {
            this.error = err.friendly
          } else {
            this.error = { lines: ['Infelizmente tivemos um problema ao alterar sua recarga automática'], support: true }
          }
        })
    },
    isRechargeAmountInRange () {
      return this.rechargeAmountNumber >= MIN_RECHARGE_AMOUNT && this.rechargeAmountNumber <= MAX_RECHARGE_AMOUNT
    },
    getAmountByPosition (position) {
      return CHARGE_AMOUNTS[CHARGE_AMOUNT_POSITIONS.findIndex(v => parseInt(v) === parseInt(position))]
    },
    getPositionByAmount (amount) {
      const positionIndex = CHARGE_AMOUNTS.reduce((findIndex, tier, index) => {
        if (parseInt(amount) >= parseInt(tier)) {
          findIndex = index
        }
        return findIndex
      }, 0)
      return positionIndex
    },
    getAmountPositionByAmount (amount) {
      return CHARGE_AMOUNT_POSITIONS[this.getPositionByAmount(amount)]
    },
    buildSlider () {
      $(`#automaticRechargeSliderEdit`).slider({
        value: this.getAmountPositionByAmount(this.rechargeAmountNumber),
        ticks: this.sliderTicks,
        ticks_positions: this.sliderTicksPositions,
        ticks_labels: this.sliderTicksLabels,
        ticks_snap_bounds: this.sliderTicksSnapBounds
      }).on('slide', (slideEvt) => {
        this.rechargeAmountNumber = this.getAmountByPosition(slideEvt.value)
      })
    },
    sliderClick () {
      this.rechargeAmountNumber = this.getAmountByPosition($(`#automaticRechargeSliderEdit`)[0].value)
    },
    openEditAutomaticRechargeModal () {
      $(this.$refs.editAutomaticRechargeModal).modal('show')
    },
    closeEditAutomaticRechargeModal () {
      $(this.$refs.editAutomaticRechargeModal).modal('hide')
      if (this.lastRecharge) {
        this.minimumAmount = Number(this.lastRecharge.minimumAmount.split(',')[0])
        this.rechargeAmount = Number(this.lastRecharge.rechargeAmount.split(',')[0])
      }
    },
    parseAmountToNumber (amount) {
      amount = amount.replace(/\./g, '').replace(',', '.')

      const amountParsed = Number(amount)
      if (isNaN(amountParsed)) {
        return amount
      }
      return amountParsed
    },
    mobileNoSavingUsage () {
      const price = this.isRatePlanNew ? NEW_MOBILE_PRICES[0] : (this.isRateplanLegacy ? LEGACY_MOBILE_PRICES[0] : MOBILE_PRICES[0])
      const mobileTariff = Number(price.replace(',', '.'))
      if (!isNaN(mobileTariff)) return mobileTariff * 1000
      return 0
    },
    fixedlineNoSavingUsage () {
      const price = this.isRatePlanNew ? NEW_FIXEDLINE_PRICE : (this.isRateplanLegacy ? LEGACY_FIXEDLINE_PRICES[0] : FIXEDLINE_PRICE)
      const fixedlineTariff = Number(price.replace(',', '.'))
      if (!isNaN(fixedlineTariff)) return fixedlineTariff * 1000
      return 0
    },
    mobileSavingUsage () {
      const amountPosition = this.getPositionByAmount(this.rechargeAmountNumber)
      const price = this.isRatePlanNew ? NEW_MOBILE_PRICES[amountPosition] : (this.isRateplanLegacy ? LEGACY_MOBILE_PRICES[amountPosition] : MOBILE_PRICES[amountPosition])
      const mobileTariff = Number(price.replace(',', '.'))
      if (!isNaN(mobileTariff)) return mobileTariff * 1000
      return 0
    },
    fixedlineSavingUsage () {
      const amountPosition = this.getPositionByAmount(this.rechargeAmountNumber)
      const price = this.isRatePlanNew ? NEW_FIXEDLINE_PRICE : (this.isRateplanLegacy ? LEGACY_FIXEDLINE_PRICES[amountPosition] : FIXEDLINE_PRICE)
      const fixedlineTariff = Number(price.replace(',', '.'))
      if (!isNaN(fixedlineTariff)) return fixedlineTariff * 1000
      return 0
    }
  },
  computed: {
    mobilePrice () {
      const prices = this.isRatePlanNew ? NEW_MOBILE_PRICES : (this.isRateplanLegacy ? LEGACY_MOBILE_PRICES : MOBILE_PRICES)
      return prices[this.getPositionByAmount(this.rechargeAmountNumber)]
    },
    fixedlinePrice  () {
      if (this.isRatePlanNew) return NEW_FIXEDLINE_PRICE
      if (this.isRateplanLegacy) {
        return LEGACY_FIXEDLINE_PRICES[this.getPositionByAmount(this.rechargeAmountNumber)]
      }
      return FIXEDLINE_PRICE
    },
    averageSaving () {
      const mobileNoSaving = this.mobileNoSavingUsage()
      const fixedNoSaving = this.fixedlineNoSavingUsage()
      const mobileSaving = this.mobileSavingUsage()
      const fixedSaving = this.fixedlineSavingUsage()
      const noSavingUsage = mobileNoSaving + fixedNoSaving
      const savingUsage = mobileSaving + fixedSaving
      return noSavingUsage - savingUsage
    }
  },
  watch: {
    minimumAmountNumber () {
      this.error = null
    },
    rechargeAmountNumber (amount) {
      $(`#automaticRechargeSliderEdit`).slider('setValue', this.getAmountPositionByAmount(amount))
      this.error = null
    }
  }
}
</script>

<style lang="scss">
@import '../../assets/scss/financial';

#automatic-recharge-edit {
  float: left;
}

.ar-edit-modal {
  max-width: 620px;
  margin-left: auto;
  margin-right: auto;

  @include financial-modal-content;

  .ar-edit-modal-header {
    @include financial-modal-header;
  }

  .ar-edit-modal-footer {
    @include financial-modal-footer;
  }

  .ar-edit-btn-cancel {
    @include financial-btn-cancel;
  }

  .ar-edit-btn-submit {
    @include financial-btn-submit;
  }
}

.automaticRechargeAmount {
  text-align: center;
}

@include financial-form-labels;

@include financial-form-prices;

@include financial-form-economy;
</style>



// WEBPACK FOOTER //
// src/components/AutomaticRecharge/Edit.vue